import { getCoverageLinesText } from '@common/utils';
import { useMemo } from 'react';
import { LinkProps } from '@common-components';
import { UserRole } from 'enums';
import { useSearchSubmissionFileMetadata } from 'hooks';
import { messages } from 'i18n';
import { ActivityLog, ExtendedMarketRequest } from 'types';
import { attachmentPointText, formatBigNumber, formatPriceOrNA } from 'utils';
import { useFetchSubmissionFiles } from 'broker/pages/SubmissionWorkspacePage/hooks/useFetchSubmissionFiles';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { getFileForQuoteExtraction, getRelevantBoxItems } from 'broker/utils';
import { useStatusSpecificMetadata } from './useStatusSpecificMetadata';
import { getQuoteCoverageLines } from './utils';

export function useGetQuoteMetadataData(
  marketRequest: ExtendedMarketRequest,
  emailActivity?: ActivityLog,
): {
  title: string;
  description?: string;
  subtitle?: string;
  isAIExtracted?: boolean;
  linkProps?: Omit<LinkProps, 'children'> & { linkText?: string; linkTooltip?: string };
  titleTextAfterLink?: string;
} {
  const statusSpecificMetadata = useStatusSpecificMetadata(marketRequest, emailActivity);
  const { partialSubmission } = useSubmissionsWorkspace();

  const { items: boxItems } = useFetchSubmissionFiles();

  const { items: submissionFileMetadata } = useSearchSubmissionFileMetadata({
    filter: {
      submissionId: partialSubmission?.id,
    },
    enabled: !!partialSubmission,
  });

  const filteredFiles = useMemo(
    () => getRelevantBoxItems(boxItems, submissionFileMetadata),
    [boxItems, submissionFileMetadata],
  );

  const isAIExtracted = marketRequest.quote?.updatedByUserDetails?.role === UserRole.Machine;

  const marketQuoteBoxItems = filteredFiles.filter((item) => marketRequest.quote?.marketQuoteFileIds.includes(item.id));

  const relevantFile = getFileForQuoteExtraction(marketQuoteBoxItems);

  const premium = isAIExtracted
    ? relevantFile?.syncMetadata?.extractedData?.premium?.value
    : marketRequest.quote?.premium;

  const aggregateLimit = isAIExtracted
    ? relevantFile?.syncMetadata?.extractedData?.aggregateLimit?.value
    : marketRequest.quote?.limit;

  // Some statuses metadata is unique, if this is the case, return it, otherwise fallback to the default behavior
  if (statusSpecificMetadata) {
    return statusSpecificMetadata;
  }

  if (!marketRequest.quote) {
    return { title: '', subtitle: '' };
  }

  // Build default quote summary
  const detailsArray = [
    marketRequest.layer?.attachmentPoint
      ? `${attachmentPointText(marketRequest.layer?.attachmentPoint)}`
      : messages.general.primary,
    premium ? `${messages.general.premium}: ${formatPriceOrNA(Number(premium))}` : '',
    aggregateLimit ? `${messages.general.aggregateLimit}: $${formatBigNumber(aggregateLimit)}` : '',
  ];

  const quoteCoverageLines = getQuoteCoverageLines(marketRequest);
  if (quoteCoverageLines.length > 0) {
    detailsArray.push(`${messages.general.coverageLines}: ${getCoverageLinesText(quoteCoverageLines)}`);
  }

  return {
    title: `${detailsArray.filter(Boolean).join(' | ')}`,
    description: '',
    subtitle: '',
    isAIExtracted,
  };
}
